<template>
    <Head title="Моя статистика" />

    <MainLayout>
        <div class="stats-page">
            <PlayerStatsHero
                :player="heroPlayer"
                :season-name="activeSeasonName"
                :position-slug="position"
            />

            <PeriodFilter
                :seasons="seasons"
                :season-id="periodSeasonId"
                :competition-types-selected="periodCompetitions"
                :matches-played="currentStats.matches_played"
                :sets-played="currentStats.sets_played"
                @update:season-id="onSeasonIdChange"
                @update:competition-types="onCompetitionsChange"
                @open-analytics="analyticsOpen = true"
            />

            <PositionOverview :stats="currentStats" :position="position" />

            <PersonalRecords :records="personalRecords" :position="position" />

            <SkillAccordion :stats="currentStats" :position="position" />

            <AnalyticsDrawer
                :open="analyticsOpen"
                :seasons="seasons"
                :matches="matches"
                :filter-data="filterData"
                :filters="currentFilters"
                :stats="currentStats"
                :chart-data="currentChartData"
                :match-rows="matchRows"
                :position="position"
                @close="analyticsOpen = false"
                @filter-change="onAnalyticsFilterChange"
                @export="onExport"
            />
        </div>
    </MainLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Head } from '@inertiajs/vue3';
import MainLayout from '@/components/layout/MainLayout.vue';
import PlayerStatsHero from '@/components/stats/PlayerStatsHero.vue';
import PeriodFilter from '@/components/stats/PeriodFilter.vue';
import PositionOverview from '@/components/stats/PositionOverview.vue';
import PersonalRecords from '@/components/stats/PersonalRecords.vue';
import SkillAccordion from '@/components/stats/SkillAccordion.vue';
import AnalyticsDrawer from '@/components/stats/AnalyticsDrawer.vue';
import {
    getEmptyStats,
    getCurrentSeason,
    getDefaultFilters,
    buildFiltersFromPeriod,
    computeStatsBundle,
    getMatchStatsRows,
} from '@/utils/playerStatsCalculator';

const props = defineProps({
    player: Object,
    mediaBySeason: { type: Object, default: () => ({}) },
    matches: Array,
    matchesBySeason: Array,
    seasons: Array,
    actions: Array,
    setsPlayedData: Object,
    filterData: Object,
    position: String,
});

const analyticsOpen = ref(false);
const currentStats = ref(getEmptyStats());
const currentChartData = ref([]);
const personalRecords = ref({});
const currentFilters = ref({});

const initialSeason = getCurrentSeason(props.seasons);
const periodSeasonId = ref(initialSeason?.id ?? null);
const periodCompetitions = ref(['championship', 'cup', 'supercup']);

const activeSeasonName = computed(() => {
    const s = props.seasons?.find((x) => x.id === periodSeasonId.value);
    return s?.name_full || s?.name || '';
});

const heroPlayer = computed(() => {
    const seasonId = periodSeasonId.value;
    if (!seasonId) {
        return props.player;
    }

    const media = props.mediaBySeason?.[seasonId]
        ?? props.mediaBySeason?.[String(seasonId)];

    if (!media) {
        return props.player;
    }

    return {
        ...props.player,
        photo_url: media.photo_url ?? props.player?.photo_url,
        team: media.team ?? props.player?.team,
        team_short: media.team_short ?? props.player?.team_short,
        team_url: media.team_url ?? props.player?.team_url,
        team_logo: media.team_logo ?? props.player?.team_logo,
        number: media.number ?? props.player?.number,
        position: media.position ?? props.player?.position,
        position_slug: media.position_slug ?? props.player?.position_slug,
    };
});

const matchRows = computed(() => getMatchStatsRows(currentChartData.value, props.matches));

const applyFilters = (filters) => {
    currentFilters.value = { ...filters };

    if (!props.actions?.length) {
        currentStats.value = getEmptyStats();
        currentChartData.value = [];
        personalRecords.value = {};
        return;
    }

    const bundle = computeStatsBundle(
        props.actions,
        filters,
        props.matches,
        props.setsPlayedData,
        props.position
    );
    currentStats.value = bundle.stats;
    currentChartData.value = bundle.chartData;
    personalRecords.value = bundle.records;
};

const applyPeriodFilters = () => {
    const filters = buildFiltersFromPeriod({
        seasonId: periodSeasonId.value,
        competitionTypes: periodCompetitions.value,
        matches: props.matches,
    });
    applyFilters(filters);
};

const onSeasonIdChange = (id) => {
    periodSeasonId.value = id;
    applyPeriodFilters();
};

const onCompetitionsChange = (types) => {
    periodCompetitions.value = types;
    applyPeriodFilters();
};

const onAnalyticsFilterChange = (filters) => {
    if (filters.season_ids?.length === 1) {
        periodSeasonId.value = filters.season_ids[0];
    }
    if (filters.competition_types?.length) {
        periodCompetitions.value = [...filters.competition_types];
    }
    applyFilters(filters);
};

const onExport = (filters) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((v) => params.append(`${key}[]`, v));
        } else if (value) {
            params.set(key, value);
        }
    });
    window.location.href = `/player/stats/export?${params.toString()}`;
};

onMounted(() => {
    if (props.actions?.length) {
        currentFilters.value = getDefaultFilters(props.seasons, props.matchesBySeason, props.matches);
        applyPeriodFilters();
    } else {
        currentStats.value = getEmptyStats();
    }
});
</script>
